import {
  applyBrandingOverrides,
  fromCinnamonConfig,
  fromMuiPalette,
  isCinnamonConfig,
  mergeTextLibraries,
  parseStorefrontConfig,
  type MuiPalette,
  type StorefrontConfig,
  type Tokens,
} from '@commerce/tokens'
import { defaultTextsFor } from '@commerce/core'

export interface LoadedConfig {
  config: StorefrontConfig
  tokens: Tokens
}

/** commerce-scs wraps payloads in { data: { data: ... } }. */
const unwrap = (payload: unknown): unknown => {
  const outer = payload as { data?: { data?: unknown } } | undefined
  return outer?.data?.data ?? {}
}

// `applyBrandingOverrides` ships from `@commerce/tokens` rather than living here:
// it is a pure `(Tokens, StorefrontConfig) => Tokens` projection, so keeping it
// in `web` would mean the React Native shell reimplements branding.

/**
 * The copy library for a cinnamon config's own locale.
 *
 * Read straight off the raw payload rather than off the parsed config, because
 * the parse is what needs the texts — `options.localization` is where cinnamon
 * keeps the language, and an absent one falls back to English inside
 * `defaultTextsFor`.
 */
const localeTextsFor = (raw: unknown): Record<string, Record<string, string>> => {
  const options = (raw as { options?: { localization?: unknown } } | undefined)?.options
  const locale = typeof options?.localization === 'string' ? options.localization : 'en'
  return defaultTextsFor(locale)
}

/**
 * A config in the new schema needs the same treatment a cinnamon one gets.
 *
 * `texts` defaults to `{}` in the schema, so an authored config that says
 * nothing about copy leaves `texts.promotions.directDefaultLabel` undefined and
 * every promotion label renders blank. The port's defaults are the baseline
 * library, and anything the storefront authored wins key by key — the same rule
 * `fromCinnamonConfig` applies, from the same implementation.
 */
const withDefaultTexts = (config: StorefrontConfig): StorefrontConfig => ({
  ...config,
  texts: mergeTextLibraries(defaultTextsFor(config.commerce.locale), config.texts),
})

export const loadConfig = async (slug: string): Promise<LoadedConfig> => {
  const base = import.meta.env.VITE_CONFIG_API_URL
  const response = await fetch(`${base}${slug}`)
  if (!response.ok) {
    throw new Error(`Config fetch failed for "${slug}" with status ${response.status}`)
  }

  const raw = unwrap(await response.json())
  // Fail loud here: FORCE_VALIDATION is false in every commerce-scs env,
  // so this parse is the only gate between a bad config and broken UI.
  //
  // Two formats reach this point. commerce-scs serves cinnamon's, which needs
  // renaming onto the new schema and needs its copy library supplied, because
  // the served config carries none. A config already in the new schema — the
  // local dev stub, and eventually the real thing — is parsed directly.
  const config = isCinnamonConfig(raw)
    ? fromCinnamonConfig(raw, { defaultTexts: localeTextsFor(raw) })
    : withDefaultTexts(parseStorefrontConfig(raw))

  // The stored MUI palette is the inherited baseline; authored `branding` wins.
  //
  // The mode is resolved up front and handed to `fromMuiPalette`, because it
  // selects which baseline the slots nobody authored fall back to. Letting
  // `applyBrandingOverrides` flip it afterwards would leave a dark storefront
  // with light-mode text — black on near-black.
  const stored = (raw as { theme?: { palette?: MuiPalette } }).theme
  const mode = config.branding.mode ?? (stored?.palette?.mode === 'dark' ? 'dark' : 'light')
  const tokens = applyBrandingOverrides(fromMuiPalette(stored?.palette, mode), config)

  return { config, tokens }
}
